import type { CSSObject } from '@emotion/react'
import { PRIMARY_PAGE_FILL_COLOR_VAR } from '../../context/CustomizationContextProvider'
import { PAGE_WIDTH_VAR, ScrollingLayout } from '../../style/scrollingLayout'

/** Matches the variant picker, which already opened as a centred modal. */
export const OVERLAY_MODAL_WIDTH_PX = 575

const VIEWPORT_MARGIN_PX = 20
const CORNER_RADIUS_PX = 12
const PANEL_SHADOW = '0 24px 64px rgba(0, 0, 0, 0.35)'

export const OVERLAY_CLOSE_BUTTON = {
    SIZE_PX: 32,
    INSET_PX: 12,
}

/**
 * Belongs on what a panel puts below the close button, not on the panel: the
 * button anchors to the panel's top edge, so padding there would move it too.
 */
export const overlayModalCloseClearance: CSSObject = {
    paddingTop:
        OVERLAY_CLOSE_BUTTON.SIZE_PX + OVERLAY_CLOSE_BUTTON.INSET_PX * 2,
}

/** Dims the page and centres the panel. Belongs on the fixed overlay element. */
export const overlayModalScrim: CSSObject = {
    background: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingBlock: VIEWPORT_MARGIN_PX,
    boxSizing: 'border-box',
    pointerEvents: 'auto',
    width: '100%',
    left: 0,
    right: 0,
}

/**
 * Rebasing the page width keeps a section that bleeds inside the modal rather
 * than out at the window edge: those sections size themselves against the
 * page, not against a parent.
 */
const modalSurface: CSSObject = {
    borderRadius: CORNER_RADIUS_PX,
    boxShadow: PANEL_SHADOW,
    [PAGE_WIDTH_VAR]: `${OVERLAY_MODAL_WIDTH_PX}px`,
}

/** The panel itself. Belongs on the element the scrim centres. */
export const overlayModalPanel: CSSObject = {
    ...modalSurface,
    position: 'relative',
    inset: 'auto',
    width: OVERLAY_MODAL_WIDTH_PX,
    maxWidth: '100%',
    // A product with one image and no body copy leaves a window-tall panel
    // mostly empty, so the panel hugs its content up to the viewport.
    height: 'auto',
    maxHeight: '100%',
    overflow: 'hidden',
    background: `var(${PRIMARY_PAGE_FILL_COLOR_VAR})`,
    pointerEvents: 'auto',
}

/**
 * Places a dialog sheet absolutely inside the shared portal target, which
 * spans the window: without the centring the sheet would stretch edge to edge.
 */
export const dialogSheet: CSSObject = {
    position: 'absolute',
    inset: '15px 8px',
    [ScrollingLayout.DESKTOP_MEDIA]: {
        ...modalSurface,
        insetInline: `calc((100% - ${OVERLAY_MODAL_WIDTH_PX}px) / 2)`,
        insetBlock: '50% auto',
        transform: 'translateY(-50%)',
        maxHeight: `calc(100% - ${VIEWPORT_MARGIN_PX * 2}px)`,
    },
}
